import { Button, Input } from "@blurb/codex-react";
import { Group, MantineProvider, Stack } from "@mantine/core";
import { GraphQL, GraphQLQueryData, Rest } from "@rpi/openapi-api";
import { Flex, Grid, IconV3, RpiButton, RpiCheckbox, RpiLink, RpiSlider, RpiText } from "@rpi/openapi-core";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useOutlet } from "react-router-dom";
import { AppPage } from "../../components/AppPage";
import { OverflowX } from "../../components/OverflowX";
import { ProductOptionCard } from "../../components/ProductOptionCard";
import { RpiPagination } from "../../components/RpiPagination";
import { RpiSearchFilter } from "../../components/search-filter/RpiSearchFilter";
import { useCustomerProductFilters } from "../../configuration/filters/customerProduct.filter";
import { useAuthUser } from "../../hooks/useAuthUser";
import { useNavigateRoute } from "../../hooks/useNavigateRoute";
import { useRpiPagination } from "../../hooks/usePagination";
import { rpaTheme } from "../../theme/rpaTheme";
import { SearchGeneral, SearchInputs, SearchSort } from "../../utils/graphql.util";
import { PublicEmail } from "../../utils/link.util";
import { MailTo } from "../../utils/mail.util";
import { formatCurrency } from "../../utils/misc.util";
import { isNumber } from "../../utils/type.util";
import { CustomerProductCreateViewV2, path as createPath } from "./CustomerProductCreateView";
import { PricingCalculator } from "../index/PricingCalculator";
import { CustomerProductsTableV1 } from "./CustomerProductsTableV1";
import { CustomerProductsTableV2, CustomerProductsTableFooter } from "./CustomerProductsTableV2";
import { useCustomerProducts } from "../../hooks/useCustomerProducts";
import { PaginationV2 } from "../../components/PaginationV2";
import { useForm } from "@mantine/form";
import { CustomerProductFilter, path as filterPath } from "./CustomerProductFilter";
import { featureFlags } from "../../featureFlags";
import { CustomerProductViewV2 } from "./CustomerProductView";
import { useDrawerRoute } from "../../hooks/useDrawerRoute";

export const CustomerProductsView: React.FC = () => {
  const user = useAuthUser();
  const outlet = useOutlet();

  const createDrawerRoute = useDrawerRoute(createPath);
  const detailsDrawerRoute = useDrawerRoute("", /customer-products\/\d+\/?$/);
  const filterDrawerRoute = useDrawerRoute(filterPath);
  const editDrawerRoute = useDrawerRoute("", /\d+\/edit\/?$/);
  const [isPriceCalculatorOpen, setIsPriceCalculatorOpen] = React.useState(false);

  const canModify = user.is.CUSTOMER_ADMIN || user.is.SUPER_ADMIN;

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.CustomerProductsQueryVariables>>({
    by: GraphQL.CustomerProductSearchField.Id,
    sortDirection: GraphQL.SortDirection.Desc,
  });
  const [searchInputs, setSearchInputs] = React.useState<SearchInputs<GraphQL.CustomerProductsQueryVariables>>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.CustomerProductsQueryVariables>>();

  const pagination = useRpiPagination({ perPage: 20 });

  const customerProductFilters = useCustomerProductFilters();

  const { data, error, isLoading, refetch } = useCustomerProducts(user.is.SUPER_ADMIN, pagination, {
    inputs: searchInputs,
    sort: sortInput,
    generalInput: searchGeneral,
  });

  const customerProducts = data?.customerProducts.data;

  const { data: openApiProductsData, ...openApiProductsQuery } = GraphQL.useListOpenApiProductsQuery(
    { hideAlreadyAssociatedProducts: true },
    { enabled: canModify }
  );
  const openApiProducts = React.useMemo(() => openApiProductsData?.listOpenApiProducts, [openApiProductsData]);

  const { data: customerData } = GraphQL.useCustomerQuery({ customerPublicId: user.id! });
  const customer = React.useMemo(() => customerData?.customer, [customerData]);

  React.useEffect(() => {
    if (outlet === null) {
      refetch();
      openApiProductsQuery.refetch();
    }
  }, [outlet]);

  const maximumOrdersMessage = React.useMemo(() => {
    if (!customer) return;

    const { maxOrders } = customer?.settings?.ordersLimit || {};

    return (
      <>
        {!isNumber(maxOrders)
          ? ""
          : maxOrders > 0
          ? `You can place up to ${maxOrders} orders in a day. `
          : "You can place unlimited amount of orders in a day. "}
        {!isNumber(maxOrders)
          ? "If you want to set order limit"
          : maxOrders > 0
          ? "If you want to increase this number"
          : "If you want to limit this number"}
        ,
        <br />
        please reach out to RPI CS team{" "}
        <RpiLink
          variant="inherit"
          href={MailTo({ email: PublicEmail.SUPPORT, subject: `Increase order limit for "${customer.publicId}"` })}
          children={PublicEmail.SUPPORT}
        />
        .
      </>
    );
  }, [customer]);

  const [productId, setProductId] = React.useState<number | undefined>(undefined);

  const form = useForm<{ searchValue: string }>({
    initialValues: {
      searchValue: "",
    },
  });

  const handleSubmit = (input: { searchValue: string }) => {
    setSearchGeneral(input.searchValue);
  };

  return (
    <AppPage path={["Products"]} onReload={refetch}>
      {featureFlags.newProductCreate ? (
        <MantineProvider theme={rpaTheme}>
          <CustomerProductCreateViewV2 isOpened={createDrawerRoute.isOpen} onClose={createDrawerRoute.close} />
          <PricingCalculator
            isOpened={isPriceCalculatorOpen}
            onClose={() => setIsPriceCalculatorOpen(false)}
            productId={productId}
            customerProducts={customerProducts}
          />
        </MantineProvider>
      ) : (
        createDrawerRoute.isOpen && outlet
      )}
      {featureFlags.newTableView && user.is.SUPER_ADMIN && (
        <MantineProvider theme={rpaTheme}>
          <CustomerProductFilter
            isOpened={filterDrawerRoute.isOpen}
            onClose={filterDrawerRoute.close}
            filters={searchInputs}
            onApply={setSearchInputs}
          />
        </MantineProvider>
      )}
      {featureFlags.newTableView ? (
        <MantineProvider theme={rpaTheme}>
          <CustomerProductViewV2 isOpened={detailsDrawerRoute.isOpen} onClose={detailsDrawerRoute.close} />
        </MantineProvider>
      ) : (
        detailsDrawerRoute.isOpen && outlet
      )}
      {editDrawerRoute.isOpen && outlet}
      <AppPage.Section>
        <Group position={"apart"}>
          {featureFlags.newTableView ? (
            <MantineProvider theme={rpaTheme}>
              <Group spacing="sm">
                <form onSubmit={form.onSubmit(handleSubmit)}>
                  <Group spacing="sm">
                    <Input
                      style={{ width: 502 }}
                      placeholder="Search by Product Name or SKU"
                      {...form.getInputProps("searchValue")}
                    />
                    <Button variant="outlined" intent="neutral" type="submit" data-testid="search-button">
                      Search
                    </Button>
                  </Group>
                </form>
                {user.is.SUPER_ADMIN && (
                  <Button variant="outlined" intent="neutral" onClick={filterDrawerRoute.open}>
                    <IconV3 icon="tune" color="inherit" size={20} />
                    Filters
                  </Button>
                )}
              </Group>
            </MantineProvider>
          ) : (
            <RpiSearchFilter
              placeholder="Search by Product Name or SKU"
              filters={customerProductFilters}
              onChange={setSearchInputs}
              onSearch={setSearchGeneral}
            />
          )}

          {featureFlags.newProductCreate && (
            <MantineProvider theme={rpaTheme}>
              <Button
                variant="filled"
                intent="brand"
                onClick={createDrawerRoute.open}
                data-testid="addNewProductButton"
              >
                <Group spacing={"xs"}>
                  <IconV3 icon="add" color="inherit" /> Add New Product
                </Group>
              </Button>
            </MantineProvider>
          )}
        </Group>
        {featureFlags.newTableView ? (
          <MantineProvider theme={rpaTheme}>
            <CustomerProductsTableV2
              customerProducts={customerProducts}
              isLoading={isLoading}
              error={error}
              searchInputs={searchInputs}
              searchGeneral={searchGeneral}
              setIsPriceCalculatorOpen={setIsPriceCalculatorOpen}
              setProductId={setProductId}
            >
              <CustomerProductsTableFooter>
                <PaginationV2 pagination={pagination} />
              </CustomerProductsTableFooter>
            </CustomerProductsTableV2>
          </MantineProvider>
        ) : (
          <Stack>
            <CustomerProductsTableV1
              customerProducts={customerProducts}
              isLoading={isLoading}
              error={error}
              searchInputs={searchInputs}
              searchGeneral={searchGeneral}
              setSortInput={setSortInput}
              pagination={pagination}
              setIsPriceCalculatorOpen={() => setIsPriceCalculatorOpen(true)}
              setProductId={setProductId}
            />
            <Flex.Row fullWidth justify="space-between">
              <RpiText
                type="p3"
                weight="regular"
                color={(theme) => theme.colors.brand[5]}
                children={maximumOrdersMessage}
              />
              <RpiPagination centered={false} pagination={pagination} />
            </Flex.Row>
          </Stack>
        )}
      </AppPage.Section>

      {!featureFlags.newProductCreate && canModify && <AddNewProduct openApiProducts={openApiProducts} />}
    </AppPage>
  );
};

interface AddNewProductProps {
  openApiProducts?: GraphQLQueryData<GraphQL.ListOpenApiProductsQuery>;
}

const AddNewProduct: React.FC<AddNewProductProps> = ({ openApiProducts }) => {
  const navigateRoute = useNavigateRoute();

  const [selectedSize, setSelectedSize] = React.useState<Rest.ProductListingResponse["sizes"][string][number]>();
  const [selectedCover, setSelectedCover] = React.useState<Rest.ProductListingResponse["covers"][number]>();
  const [selectedPaper, setSelectedPaper] = React.useState<Rest.ProductListingResponse["papers"][number]>();

  const [pageQuantity, setPageQuantity] = React.useState(20);

  const { data: priceListInfoData } = useQuery(["price-list-info"], Rest.getPriceListInfo);
  const priceListInfo = React.useMemo(() => priceListInfoData?.data, [priceListInfoData]);

  const { data: productListingData } = useQuery(["product-listing"], Rest.getProductListing, {
    onSuccess: ({ sizes, covers, papers }) => {
      setSelectedSize((selected) => (selected ? selected : sizes[Object.keys(sizes)[0]][0]));
      setSelectedCover((selected) => (selected ? selected : covers[0]));
      setSelectedPaper((selected) => (selected ? selected : papers[0]));
    },
  });
  const { sizes, covers, papers } = React.useMemo(
    () => (productListingData || {}) as Rest.ProductListingResponse,
    [productListingData]
  );

  const selectedPriceListing = React.useMemo(() => {
    if (!productListingData || !selectedSize || !selectedCover || !selectedPaper) return;

    const sku = productListingData.pricing_sku_mapping.find(
      (map) =>
        map.size === selectedSize.price_key &&
        map.cover === selectedCover.price_key &&
        map.paper === selectedPaper?.price_key
    )?.sku;

    if (!sku) return;
    return priceListInfo?.products.find((product) => product.openapiSku === sku);
  }, [productListingData, priceListInfo, selectedSize, selectedCover, selectedPaper]);

  const isProductAssociated = React.useMemo(() => {
    if (!openApiProducts) return false;
    if (!selectedPriceListing?.openapiSku) return false;
    return !openApiProducts.find((oap) => oap.openapiSku === selectedPriceListing.openapiSku);
  }, [openApiProducts, selectedPriceListing]);

  const selectedPrice = React.useMemo(() => {
    if (!selectedPriceListing) return 0;

    const extraPages =
      pageQuantity < selectedPriceListing.minPageCount
        ? selectedPriceListing.minPageCount
        : pageQuantity - selectedPriceListing.minPageCount;

    return selectedPriceListing.basePriceUsd + extraPages * selectedPriceListing.pageCostUsd;
  }, [selectedPriceListing, pageQuantity]);

  React.useEffect(() => {
    if (selectedPriceListing && pageQuantity < selectedPriceListing.minPageCount) {
      setPageQuantity(selectedPriceListing.minPageCount);
    }
  }, [selectedPriceListing, pageQuantity]);

  return (
    <AppPage.Section mt={40} title="Add a new product" isLoading={!productListingData || !priceListInfo}>
      {productListingData && priceListInfo && (
        <Flex.Column gap={32} sx={(theme) => ({ color: theme.colors.brand[5], flex: 1, position: "relative" })}>
          <Flex.Column gap={24}>
            <RpiText type="h3" weight="bold" children="1. Choose a book size" />
            <OverflowX>
              <Flex.Row
                gap={16}
                children={
                  sizes &&
                  Object.keys(sizes).map((key, i) => {
                    const options = sizes[key];

                    return (
                      <ProductOptionCard
                        key={`size-option-${i}`}
                        src={`/assets/images/book-sizes/${key}_size.png`}
                        variants={options.map((option) => ({
                          name: option.inches,
                          description: [option.centimeters],
                          onClick: () => setSelectedSize(option),
                          actionNode: (
                            <RpiCheckbox
                              mt={7}
                              checked={selectedSize?.price_key === option.price_key}
                              readOnly
                              variant="radio"
                            />
                          ),
                        }))}
                      />
                    );
                  })
                }
              />
            </OverflowX>
          </Flex.Column>

          <Flex.Column gap={24}>
            <RpiText type="h3" weight="bold" children="2. Choose a cover for your book" />
            <OverflowX>
              <Flex.Row
                gap={16}
                children={covers?.map((cover, i) => {
                  const isDisabled = cover.price_key === "SC" && selectedSize?.price_key === "12x12";
                  const isSelected = selectedCover?.price_key === cover.price_key;

                  if (isDisabled && isSelected) setSelectedCover(undefined);

                  return (
                    <ProductOptionCard
                      key={`cover-option-${i}`}
                      src={`/assets/images/book-covers/${cover.price_key}_cover.png`}
                      variants={[
                        {
                          name: cover.name,
                          description: cover.description_lines,
                          bullet: true,
                          disabled: isDisabled,
                          onClick: !isDisabled ? () => setSelectedCover(cover) : undefined,
                          actionNode: (
                            <RpiCheckbox mt={7} checked={isSelected} readOnly variant="radio" disabled={isDisabled} />
                          ),
                        },
                      ]}
                    />
                  );
                })}
              />
            </OverflowX>
          </Flex.Column>

          <Flex.Column gap={24} fullWidth>
            <RpiText type="h3" weight="bold" children="3. Choose the paper type" />
            <OverflowX>
              <Flex.Row
                fullWidth
                gap={16}
                children={papers?.map((paper, i) => (
                  <ProductOptionCard
                    key={`paper-option-${i}`}
                    src={`/assets/images/book-papers/${paper.price_key}_paper.png`}
                    size={papers?.length > 1 ? "sm" : "lg"}
                    variants={[
                      {
                        name: paper.name,
                        description: paper.description_lines,
                        bullet: true,
                        onClick: () => setSelectedPaper(paper),
                        actionNode: (
                          <RpiCheckbox
                            mt={7}
                            checked={selectedPaper?.price_key === paper.price_key}
                            readOnly
                            variant="radio"
                          />
                        ),
                      },
                    ]}
                  />
                ))}
              />
            </OverflowX>
          </Flex.Column>

          <Grid
            px={28}
            py={32}
            gap={24}
            columns="1fr 1fr"
            sx={(theme) => ({
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: theme.colors.brand[5],
              borderRadius: 24,
              maxWidth: 1123,
              width: "100%",
            })}
          >
            <RpiText type="h2" weight="bold" sx={{ gridColumn: "span 2" }} children="Product summary" />

            <div>
              <RpiText type="p3" weight="light" children="Photo book size" />
              <RpiText
                type="p1"
                weight="regular"
                sx={{ minHeight: 24 }}
                children={selectedSize && `${selectedSize.inches} | ${selectedSize.centimeters}`}
              />
            </div>

            <Flex.Column justify="space-between" sx={{ gridRow: "span 2" }}>
              <RpiText type="p2" weight="regular" children="Choose page quantity" />

              <RpiSlider
                mb={16}
                min={selectedPriceListing ? selectedPriceListing.minPageCount : 20}
                max={380}
                marks={[
                  { value: selectedPriceListing ? selectedPriceListing.minPageCount : 20, label: 20 },
                  { value: 380, label: 380 },
                ]}
                value={pageQuantity}
                onChange={setPageQuantity}
              />
            </Flex.Column>

            <div>
              <RpiText type="p3" weight="light" children="Cover" />
              <RpiText type="p1" weight="regular" sx={{ minHeight: 24 }} children={selectedCover?.name} />
            </div>

            <div>
              <RpiText type="p3" weight="light" children="Paper type" />
              <RpiText type="p1" weight="regular" sx={{ minHeight: 24 }} children={selectedPaper?.name} />
            </div>

            <Flex.Column gap={24}>
              <RpiText
                type="h1"
                weight="bold"
                color={(theme) => theme.colors.spark[5]}
                children={`From US ${formatCurrency(GraphQL.SupportedCurrency.Usd, selectedPrice)}`}
              />
              <RpiText type="p3" weight="regular" children="Cost per book does not include taxes and shipping." />

              <Flex.Row gap={16} align="baseline">
                <RpiButton
                  variant="mellow"
                  trailingIcon
                  width="fit"
                  disabled={!selectedSize || !selectedCover || !selectedPaper || !selectedPrice || isProductAssociated}
                  children="Add product"
                  onClick={() =>
                    navigateRoute(["customer-product:create"], { state: { sku: selectedPriceListing?.openapiSku } })
                  }
                />

                {isProductAssociated && (
                  <RpiText
                    type="p3"
                    weight="regular"
                    color={(theme) => theme.colors.spark[5]}
                    children="This product was already added."
                  />
                )}
              </Flex.Row>
            </Flex.Column>
          </Grid>
        </Flex.Column>
      )}
    </AppPage.Section>
  );
};
